import React from "react";
import { PageHero } from "../components/site/Shared";
import { Reveal } from "../components/site/Reveal";
import { PrimaryButton } from "../components/site/Buttons";
import { METHOD, IMAGES } from "../data/content";

const DIMENSIONS = [
  {
    title: "Leadership & Storytelling",
    body: "Leadership is expressed through decisions, communication, framing, narrative, and presence. Yadid helps leaders understand how their choices and words will be received and how to communicate with clarity across different environments.",
  },
  {
    title: "Connections & Relationships",
    body: "Strategy travels through people. Yadid helps leaders understand relationship landscapes, develop trusted pathways, strengthen credibility, and steward relationships capable of carrying long-term partnership.",
  },
];

export default function HowWeAdvise() {
  return (
    <div data-testid="how-we-advise-page">
      <PageHero
        eyebrow="Our Philosophy"
        titleLines={["Advice begins", "with understanding."]}
        lede="Yadid does not begin with a framework and force the situation into it. We begin by listening. Because before a leader can move wisely, someone must understand what is actually happening."
      />

      <section className="bg-stone-soft/40 border-y border-stone">
        <div className="yadid-container py-16 md:py-20">
          <Reveal>
            <p className="text-lg md:text-xl text-ink/70 font-light leading-relaxed max-w-4xl">
              Our advisory philosophy is expressed through four movements:{" "}
              <span className="text-ink font-normal">Listen. Reveal. Reimagine. Guide.</span> These
              are not rigid phases. They are disciplines of trusted advisory that can deepen, repeat,
              and evolve as circumstances change.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Method chapters */}
      <section className="bg-ivory">
        <div className="yadid-container">
          {METHOD.map((m, i) => (
            <div
              key={m.title}
              className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 py-16 md:py-24 border-b border-stone"
            >
              <div className="lg:col-span-4">
                <Reveal>
                  <span className="font-serif text-brass text-2xl">{m.n}</span>
                  <h2 className="mt-4 font-serif text-4xl md:text-5xl lg:text-6xl tracking-[-0.02em] text-ink">
                    {m.title}
                  </h2>
                  <p className="mt-6 font-serif text-2xl md:text-3xl text-ink/80 italic leading-snug">
                    {m.lede}
                  </p>
                </Reveal>
              </div>
              <div className="lg:col-span-7 lg:col-start-6">
                <Reveal delay={0.1}>
                  <p className="text-lg text-ink/70 font-light leading-relaxed">{m.body}</p>
                  {m.list && (
                    <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
                      {m.list.map((item) => (
                        <li key={item} className="flex gap-3 items-baseline text-ink/70 font-light">
                          <span className="text-brass text-xs">&#9679;</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  <p className="mt-8 text-ink/90 font-light leading-relaxed border-l-2 border-brass pl-6">
                    {m.close}
                  </p>
                </Reveal>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Two essential dimensions */}
      <section className="bg-ink text-ivory">
        <div className="yadid-container py-24 md:py-36">
          <Reveal>
            <p className="eyebrow text-brass mb-14">Two Essential Dimensions</p>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10">
            {DIMENSIONS.map((d, i) => (
              <Reveal key={d.title} delay={i * 0.1} className="bg-ink">
                <div className="p-8 md:p-12 h-full">
                  <span className="font-serif text-brass text-xl">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="mt-6 font-serif text-3xl md:text-4xl text-ivory">{d.title}</h3>
                  <p className="mt-6 text-ivory/65 font-light leading-relaxed">{d.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Trusted Access */}
      <section className="bg-ivory">
        <div className="yadid-container py-24 md:py-36">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-5">
              <div className="relative h-[320px] md:h-[440px] overflow-hidden">
                <img
                  src={IMAGES.silhouette}
                  alt="Two figures in conversation at dusk"
                  className="w-full h-full object-cover img-treated"
                />
              </div>
            </div>
            <div className="lg:col-span-6 lg:col-start-7">
              <Reveal>
                <p className="eyebrow text-brass mb-8">Trusted Access</p>
                <h2 className="font-serif text-4xl md:text-5xl leading-[1.08] tracking-[-0.02em] text-ink">
                  Access without trust is rarely enough.
                </h2>
                <p className="mt-8 text-lg text-ink/70 font-light leading-relaxed">
                  Yadid may bring leaders closer to communities, institutions, networks, and people
                  they need to understand. But trusted access is not transactional. Credibility must
                  accompany proximity. The relationship after the introduction matters as much as the
                  introduction itself.
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* Closing */}
      <section className="bg-stone-soft/40 border-t border-stone">
        <div className="yadid-container py-28 md:py-40 text-center flex flex-col items-center">
          <Reveal>
            <h2 className="font-serif text-3xl md:text-5xl lg:text-[3.9rem] leading-[1.06] tracking-[-0.02em] text-ink max-w-4xl">
              The objective is not more advice. It is better judgment.
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-12">
              <PrimaryButton to="/contact" data-testid="hwa-closing-cta">
                Start a Conversation
              </PrimaryButton>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
